<script setup lang="ts">
import { useCommonStore } from '@/stores/modules/common'
import { useUserStore } from '@/stores/modules/user'
import type { ConsumablesType } from '@/types/mall'
import { socketEmit, socketEmitAsync } from '@/utils/web-socket'
import { watch } from 'vue'
import { ref } from 'vue'
import { ElMessage } from 'element-plus'
import { formatNumberWithCommas } from '@/utils'
import { treasuryInfoList } from '@/views/treasury/data'

const userStore = useUserStore()
const commonStore = useCommonStore()

const consumablesList = ref<ConsumablesType[]>()
const curConsumables = ref<ConsumablesType>()
const buyCount = ref(1)
async function getConsumablesList() {
  socketEmit<ConsumablesType[]>({
    type: 'getConsumablesCollection',
    callBack: (res) => {
      consumablesList.value = res.data
    },
  })
}
const buyCountMap = ref<[number, number][]>([])
async function getUserBuyCount() {
  const res = await socketEmitAsync<[number, number][]>({ type: 'getConsumablesBuyRecord' })
  buyCountMap.value = res
}
function handleBuyConsumables() {
  if (!curConsumables.value) return ElMessage.warning('未选中任何商品')
  const { id, key, price } = curConsumables.value
  const name = treasuryInfoList.find((info) => info.key === key)?.name
  if (userStore.diamond < price * buyCount.value) return ElMessage.warning('钻石不足')
  ElMessageBox.confirm(`确认购买道具【${name}】吗？本次购买花费<b>${price * buyCount.value}</b>钻石`, '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      await socketEmitAsync({ type: 'buyConsumables', data: { id, count: buyCount.value } })
      userStore.diamond -= price * buyCount.value
      const consumables = consumablesList.value!.find((item) => item.id === id)
      if (!consumables) return
      userStore.consumables[consumables.key] += buyCount.value

      switch (consumables.key) {
        case 'equippedRoutine': {
          for (let i = 0; i < buyCount.value; i++) {
            const id = userStore.equippedRoutineList.length + 1
            userStore.equippedRoutineList.push({
              id,
              name: `装备套路${id}`,
              equippedList: { weapon: '', helmet: '', armor: '', shoes: '', jewelry: '' },
              runeEquippedList: ['', '', '', '', ''],
              relicEquippedList: ['', '', '', '', ''],
              skillEquippedList: ['', '', '', '', ''],
            })
          }
        }
      }
      getUserBuyCount()
    })
    .catch(() => {})
}

watch(
  () => commonStore.isSocketInit,
  (val) => {
    if (!val) return

    getConsumablesList()
  },
)
watch(
  () => commonStore.path === 'Mall',
  () => {
    getUserBuyCount()
  },
)
</script>

<template>
  <div class="consumables">
    <p class="common-coin-info">💎 {{ formatNumberWithCommas(userStore.diamond) }}</p>
    <div class="consumables-list-wrap border-wrap">
      <h4>商品列表</h4>
      <p style="font-size: 12px">* 周限购商品每周日0点刷新</p>
      <div class="consumables-list">
        <div class="item" v-for="item of consumablesList" :key="item.id">
          <el-button
            size="small"
            type="primary"
            plain
            @click="
              () => {
                curConsumables = item
                buyCount = 1
              }
            "
            :style="{ borderColor: curConsumables?.id === item.id ? 'red' : '' }"
            >{{ treasuryInfoList.find((info) => info.key === item.key)?.name }}</el-button
          >
        </div>
      </div>
    </div>
    <div class="consumables-detail border-wrap">
      <h4>商品详情</h4>
      <p>名称：{{ treasuryInfoList.find((info) => info.key === curConsumables?.key)?.name || '未选择' }}</p>
      <p>
        说明：<span>{{ curConsumables?.intro }}</span>
      </p>
      <p>
        购买数量：
        <el-input-number
          size="small"
          :min="1"
          :max="curConsumables?.max || curConsumables?.weekMax || 99"
          v-model="buyCount"
          :precision="0"
          :disabled="!curConsumables"
        ></el-input-number>
      </p>
      <p>
        <span>价格：{{ (curConsumables?.price || 0) * buyCount }}💎</span>
        <span style="margin-left: 15px" v-show="curConsumables?.max"
          >限购：{{ buyCountMap?.find(([id]) => curConsumables?.id === id)?.[1] || 0 }} / {{ curConsumables?.max || '∞' }}</span
        >
      </p>
      <el-button type="primary" plain style="width: 100%" @click="handleBuyConsumables">购入</el-button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.consumables {
  height: 100%;
  display: flex;
  flex-flow: column nowrap;
  .border-wrap {
    padding: 10px;
  }
  .consumables-list-wrap {
    margin-bottom: 10px;
    overflow: auto;
    flex: 1;
    .consumables-list {
      display: flex;
      flex-flow: row wrap;
      justify-content: space-between;
      .item {
        width: calc(50% - 5px);
        margin-bottom: 10px;
        button {
          width: 100%;
        }
      }
    }
  }
  .consumables-detail {
    p {
      margin: 6px 0;
      font-size: 14px;
    }
  }
  :deep() {
    .el-button:focus,
    .el-button:hover {
      background-color: unset;
    }
  }
}
</style>
